// One shared voice for FinFox.
// Every part of the app pushes messages here; the bottom-right mascot is the
// single place they surface, and the header runner can pick up short jabs.
// Purely presentational — never writes portfolio, XP or streak data.

export type FinFoxMood = "smart" | "happy" | "curious" | "concerned" | "celebrating";

/** One-tap question a student answers inside the FinFox bubble. */
export type FinFoxQuestion = {
  /** What is being taught, for telemetry. */
  concept: string;
  options: { label: string; correct?: boolean }[];
  /** Shown immediately after any answer — the answer is never withheld. */
  explanation: string;
};

export type FinFoxMessage = {
  /** Stable id so the same message never stacks up twice. */
  id: string;
  mood: FinFoxMood;
  title?: string;
  body: string;
  cta?: { label: string; href: string };
  /** Short version for the running fox's speech bubble. */
  jab?: string;
  /** Troll messages are playful reactions; notices are welcome-back info. */
  kind: "notice" | "troll" | "tip" | "question";
  /** Present on `question` messages: two or three taps, instant feedback. */
  question?: FinFoxQuestion;
};

type Listener = (messages: FinFoxMessage[]) => void;

const MAX_QUEUE = 6;
const THROTTLE_MS = 90_000;

let queue: FinFoxMessage[] = [];
const listeners = new Set<Listener>();
const seenIds = new Set<string>();
let lastPushAt = 0;

const emit = () => listeners.forEach((l) => l([...queue]));

export const subscribeFinFox = (cb: Listener) => {
  listeners.add(cb);
  cb([...queue]);
  return () => {
    listeners.delete(cb);
  };
};

/**
 * Adds a message for FinFox to deliver.
 * Trolls are throttled so the fox is fun, never noisy.
 */
export const pushFinFoxMessage = (msg: FinFoxMessage): boolean => {
  if (seenIds.has(msg.id)) return false;
  if (msg.kind === "troll") {
    const now = Date.now();
    if (now - lastPushAt < THROTTLE_MS) return false;
    lastPushAt = now;
  }
  seenIds.add(msg.id);
  queue = [...queue, msg].slice(-MAX_QUEUE);
  emit();
  return true;
};

export const dismissFinFoxMessage = (id: string) => {
  queue = queue.filter((m) => m.id !== id);
  emit();
};

export const clearFinFoxMessages = () => {
  queue = [];
  emit();
};

export const getFinFoxMessages = () => [...queue];
